import React, {useEffect, useState} from 'react';
import moment from 'moment';
import {Link} from "react-router-dom";
import {observer} from "mobx-react";
import {courseApi} from "../api/courseApi";
import {mediaRecordingsApi} from "../api/mediaRecordingsApi";
import {assessmentApi} from "../api/assessmentApi";
import {usersStore} from "../store/usersStore";
import {teachersStore} from "../store/teachersStore";
import {Spinner} from "../controls/spinner";
import style from './meetingRecordingsList.scss';

export const MeetingRecordingsList = observer(function MeetingRecordingsList({meetings, emptyMessage}) {
  return (
    <>
      {meetings === null && <Spinner/>}
      {meetings && meetings.length === 0 &&
        <div className={style.empty}>{emptyMessage}</div>
      }
      {meetings && meetings.map(meeting => (
        <MeetingRecordings key={meeting._id} meeting={meeting}/>
      ))}
    </>
  );
});

export const MeetingRecordings = observer(function MeetingRecordings({meeting}) {
  const [details, setDetails] = useState(null);

  useEffect(() => {
    (async () => {
      const [recordings, course, assessments] = await Promise.all([
        mediaRecordingsApi.searchMediaRecordingsByMeetingId(meeting._id),
        meeting.courseId ? courseApi.getCourse(meeting.courseId) : Promise.resolve(null),
        assessmentApi.getAssessmentsForMeeting(meeting._id)
      ]);
      setDetails({
        courseName: course?.name,
        recordings,
        assessedUserIds: new Set(assessments.map(a => a.userId))
      });
    })();
  }, [meeting._id]);

  if (!details) return <Spinner/>;

  const {courseName, recordings, assessedUserIds} = details;
  const recordingsByUser = groupRecordingsByUser(recordings);
  const userIds = Object.keys(recordingsByUser);
  return (
    <div className={style.meeting}>
      <div className={style.meetingHeader}>
        <div className={style.courseName}>{courseName || meeting.courseId || meeting._id}</div>
        {meeting.teacherId && <div className={style.teacherName}>{getTeacherName(meeting.teacherId)}</div>}
        {meeting.startTime && <div className={style.meetingTime}>{moment(meeting.startTime).format("ddd, DD MMM YYYY · HH:mm")}</div>}
      </div>
      {userIds.length === 0 && <div className={style.empty}>No media recordings for this meeting</div>}
      {userIds.map(userId => (
        <div key={userId} className={style.userGroup}>
          <div className={style.userGroupHeader}>
            <div className={style.userName}>{getUserName(userId)}</div>
            {assessedUserIds.has(userId) &&
              <Link className={style.link} to={`/meetings/${meeting._id}/conversations/${userId}`}>View transcript</Link>
            }
          </div>
          {recordingsByUser[userId].map(recording => (
            <div key={recording._id} className={style.recording}>
              <span className={style.time}>{moment(recording.startTimeMillis).format("HH:mm:ss")}</span>
              <span className={`${style.mediaType} ${style[recording.mediaType]}`}>{recording.mediaType}</span>
              {recording.url
                ? <a href={recording.url} target="_blank" rel="noreferrer" className={style.link}>Open recording</a>
                : <span className={style.noLink}>Not available</span>
              }
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

function groupRecordingsByUser(recordings) {
  const byUser = {};
  recordings.forEach(recording => {
    if (!byUser[recording.userId]) byUser[recording.userId] = [];
    byUser[recording.userId].push(recording);
  });
  Object.values(byUser).forEach(userRecordings =>
    userRecordings.sort((a, b) => a.startTimeMillis - b.startTimeMillis)
  );
  return byUser;
}

function getUserName(userId) {
  if (userId === 'allusers') return "All users";
  return usersStore.getUserById(userId)?.name || userId;
}

function getTeacherName(teacherId) {
  return teachersStore.getTeacherById(teacherId)?.name || teacherId;
}